import { Tabs } from "radix-ui";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { LoginForm } from "./components/auth/LoginForm";
import { PeriodFilter } from "./components/dashboard/PeriodFilter";
import { ChunkErrorBoundary } from "./components/errors/ChunkErrorBoundary";
import { NotificationBell } from "./components/notifications/NotificationBell";
import { usePlatformStatus } from "./lib/PlatformStatusContext";
import { authClient, signOut, useSession } from "./lib/auth-client";
import { useSections } from "./lib/sections";
import { getSectionLabel, sectionRegistry } from "./lib/sections/registry";
import { normPeriodo, normRubro } from "./lib/sheet/utils";
import type { Distribution, RawRow, SheetRow } from "./lib/sheet/utils";
import { trackSectionView } from "./lib/usage";
import "./styles.css";

// Lazy loaders por sección — cada uno genera un chunk separado en build.
// React.lazy solo descarga el chunk cuando el panel se navega por primera vez.
// Los loaders se definen explícitamente (no via índice de objeto) para que
// TypeScript infiera que no son undefined.
const LazyResumen = React.lazy(sectionRegistry.resumen);
const LazyERTable = React.lazy(sectionRegistry.er);
const LazyCCGrid = React.lazy(sectionRegistry.centros);
const LazyRubrosTable = React.lazy(sectionRegistry.rubros);
const LazyVentas = React.lazy(sectionRegistry.ventas);
const LazyCompras = React.lazy(sectionRegistry.compras);

// DistributionPanel: se usa solo en el tab "er", se lazifica junto con él
const LazyDistributionPanel = React.lazy(() =>
  import("./components/dashboard/DistributionPanel").then((m) => ({
    default: m.DistributionPanel,
  })),
);

const PanelSpinner = () => (
  <div className="loading-overlay show">
    <div className="spinner" />
  </div>
);

export function App() {
  const { data: session, isPending } = useSession();
  const { data: activeOrg } = authClient.useActiveOrganization();

  const hasSession = !!session;

  // Detectar membership de plataforma para mostrar el botón "Consola".
  // El fetch se hace UNA sola vez en PlatformStatusProvider (main.tsx) — sin duplicado.
  const { isPlatformMember } = usePlatformStatus();

  // Secciones data-driven desde /api/sections (filtradas por registry).
  // Los platform members no tienen activeOrg → /api/sections devolvería 403.
  // Omitir la llamada si el usuario es platformMember para evitar ruido en consola.
  const sectionsState = useSections(hasSession && !isPlatformMember);

  // Primer tab activo: el primero de la lista (menor sortOrder).
  // String vacío hasta que lleguen las secciones.
  const [activeTab, setActiveTab] = useState<string>("");

  // Cuando llegan las secciones, inicializar el tab activo si aún no está seteado
  useEffect(() => {
    if (sectionsState.status === "ok" && sectionsState.sections.length > 0 && !activeTab) {
      const first = sectionsState.sections[0];
      if (first) setActiveTab(first.key);
    }
  }, [sectionsState, activeTab]);

  // Emitir section_viewed cuando el usuario cambia de sección (F3 Tel-3).
  // Solo para usuarios TENANT con sesión activa. Los platform staff viendo
  // la consola (/admin) no pasan por este componente, por lo que no hay riesgo
  // de emitir section_viewed sin org activa (el worker devolvería 204 silencioso
  // de todas formas, pero evitamos el request innecesario chequeando que haya
  // activeTab y sesión). El debounce y dedupe viven en trackSectionView.
  useEffect(() => {
    if (!hasSession || !activeTab) return;
    trackSectionView(activeTab);
  }, [activeTab, hasSession]);

  // App State
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [tenantSuspended, setTenantSuspended] = useState(false);
  const [lastSync, setLastSync] = useState<string>("");
  const [rawRows, setRawRows] = useState<RawRow[]>([]);
  const [ventasRows, setVentasRows] = useState<SheetRow[]>([]);
  const [comprasRows, setComprasRows] = useState<SheetRow[]>([]);
  const [allPeriods, setAllPeriods] = useState<string[]>([]);
  const [selectedPeriods, setSelectedPeriods] = useState<Set<string>>(new Set());
  const [ccFilter, setCcFilter] = useState("Total");
  const [tipoFilter, setTipoFilter] = useState("Todos");
  const [autoRefresh, setAutoRefresh] = useState(0);
  const [distribution, setDistribution] = useState<Distribution>({
    Merchandising: 0.5,
    Impresiones: 0.5,
    "DTF Textil": 0.0,
  });

  const fetchData = async () => {
    setLoading(true);
    setFetchError(null);
    setTenantSuspended(false);
    try {
      const apiURL = import.meta.env.VITE_API_URL || "http://localhost:6787";
      // Sesión por cookie de Better Auth (mismo site dashboard/API).
      const init: RequestInit = { credentials: "include" };

      // Fetch 'resultado'
      const res = await fetch(`${apiURL}/sheet?range=resultado`, init);

      // Detectar suspensión antes de parsear el body como datos.
      if (res.status === 403) {
        const payload = (await res.json().catch(() => ({}))) as { code?: string };
        if (payload.code === "TENANT_SUSPENDED") {
          setTenantSuspended(true);
          return;
        }
        throw new Error(`Sin acceso a los datos (${res.status})`);
      }

      if (!res.ok) {
        throw new Error(`Error al cargar datos: ${res.status}`);
      }

      const data = (await res.json()) as { values: string[][] };

      const rows: RawRow[] = data.values
        .slice(1)
        .map((r) => ({
          cuenta: String(r[0] ?? "").trim(),
          rubro: normRubro(r[1] ?? ""),
          tipo: String(r[2] || "White").trim(),
          cc: String(r[3] || "").trim(),
          periodo: normPeriodo(r[4] ?? ""),
          importe:
            Number.parseFloat(
              String(r[5] ?? "")
                .replace(/\./g, "")
                .replace(",", "."),
            ) || 0,
        }))
        .filter((r) => r.importe !== 0 && r.periodo);

      setRawRows(rows);
      const periods = [...new Set(rows.map((r) => r.periodo))].sort();
      setAllPeriods(periods);
      if (selectedPeriods.size === 0) setSelectedPeriods(new Set(periods));

      // Fetch ventas y compras
      const [vRes, cRes] = await Promise.all([
        fetch(`${apiURL}/sheet?range=fc_ventas`, init),
        fetch(`${apiURL}/sheet?range=fc_compras`, init),
      ]);

      const [vData, cResData] = (await Promise.all([vRes.json(), cRes.json()])) as [
        { values?: string[][] },
        { values?: string[][] },
      ];

      const headersToObj = (rows: string[][]): SheetRow[] => {
        if (!rows || !rows.length || !rows[0]) return [];
        const h = rows[0].map((x) => String(x).trim());
        return rows.slice(1).map((row) => {
          const obj: SheetRow = {};
          h.forEach((name, i) => {
            obj[name] = row[i] ?? null;
          });
          return obj;
        });
      };

      setVentasRows(headersToObj(vData.values || []));
      setComprasRows(headersToObj(cResData.values || []));
      setLastSync(new Date().toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }));
    } catch (err) {
      console.error("Failed to fetch data", err);
      const raw = err instanceof Error ? err.message : "Error desconocido";
      setFetchError(`Error al cargar datos: ${raw}`);
    } finally {
      setLoading(false);
    }
  };

  // Mantener la última versión de fetchData sin recrear los effects en cada render.
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  useEffect(() => {
    if (hasSession) fetchDataRef.current();
  }, [hasSession]);

  // Auto Refresh logic
  useEffect(() => {
    if (autoRefresh > 0) {
      const t = setInterval(() => fetchDataRef.current(), autoRefresh * 1000);
      return () => clearInterval(t);
    }
  }, [autoRefresh]);

  if (isPending) {
    return (
      <div className="loading-overlay show">
        <div className="spinner" />
      </div>
    );
  }

  if (!session) return <LoginForm />;

  // true si la suspensión fue detectada por fetchSections o por fetchData
  const isSuspended =
    tenantSuspended || (sectionsState.status === "error" && sectionsState.suspended === true);

  const renderNavItems = () => {
    if (isSuspended) return null;

    if (sectionsState.status === "loading") {
      return (
        <div className="nav-tab" style={{ opacity: 0.5 }} aria-busy="true">
          Cargando secciones...
        </div>
      );
    }

    if (sectionsState.status === "error") {
      return (
        <div className="nav-tab" style={{ color: "var(--color-danger, #f87171)" }} role="alert">
          No se pudieron cargar las secciones. Recargá la página.
        </div>
      );
    }

    if (sectionsState.sections.length === 0) {
      return (
        <div className="nav-tab" style={{ opacity: 0.7 }}>
          Tu organización no tiene paneles habilitados. Contactá a tu administrador.
        </div>
      );
    }

    return sectionsState.sections.map((s) => (
      <Tabs.Trigger key={s.key} value={s.key} className="nav-tab">
        {getSectionLabel(s.key)}
      </Tabs.Trigger>
    ));
  };

  return (
    <div className="dashboard-root">
      <header className="header">
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <span className="logo">◈ Tagliapietra Portal</span>
          {activeOrg?.name && (
            <span className="tenant-badge">
              <span className="tenant-name">{activeOrg.name}</span>
            </span>
          )}
          <div className="sync-status">
            <div className={`sync-dot ${loading ? "loading" : ""}`} />
            <span className="sync-label">
              {loading ? "Actualizando..." : `Actualizado ${lastSync}`}
            </span>
          </div>
        </div>
        <div className="header-actions">
          <div className="auto-refresh">
            <div className={`auto-dot ${autoRefresh > 0 ? "on" : ""}`} />
            <select
              className="filter-select"
              value={autoRefresh}
              onChange={(e) => setAutoRefresh(Number(e.target.value))}
            >
              <option value="0">Auto-refresh Off</option>
              <option value="60">cada 1 min</option>
              <option value="300">cada 5 min</option>
            </select>
          </div>
          <NotificationBell />
          <span className="user-email">{session.user.email}</span>
          {isPlatformMember && (
            <Link
              to="/admin"
              className="sync-btn console-link"
              aria-label="Abrir consola de plataforma"
            >
              Consola
            </Link>
          )}
          <button type="button" className="sync-btn" onClick={fetchData} disabled={loading}>
            Manual Sync
          </button>
          <button type="button" className="sync-btn" onClick={() => signOut()}>
            Salir
          </button>
        </div>
      </header>

      {isSuspended && (
        <div className="suspended-banner" role="alert" aria-live="assertive">
          <div className="suspended-banner-icon" aria-hidden="true">
            !
          </div>
          <div className="suspended-banner-body">
            <strong className="suspended-banner-title">Tu cuenta está suspendida</strong>
            <p className="suspended-banner-desc">
              El acceso a este portal fue deshabilitado temporalmente. Contactá al estudio
              Tagliapietra para regularizar tu situación.
            </p>
          </div>
          <button type="button" className="sync-btn" onClick={() => signOut()}>
            Salir
          </button>
        </div>
      )}

      {!isSuspended && (
        <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
          <Tabs.List className="nav" aria-label="Secciones del dashboard">
            {renderNavItems()}
          </Tabs.List>

          <div className="filterbar">
            <div className="filter-group">
              <span className="filter-label">Periodo</span>
              <PeriodFilter
                allPeriods={allPeriods}
                selectedPeriods={selectedPeriods}
                onChange={setSelectedPeriods}
                rows={rawRows}
              />
            </div>

            <div className="filter-group">
              <span className="filter-label">Centro</span>
              <select
                className="filter-select"
                value={ccFilter}
                onChange={(e) => setCcFilter(e.target.value)}
              >
                <option value="Total">Total</option>
                <option value="Merchandising">Merchandising</option>
                <option value="Impresiones">Impresiones</option>
                <option value="DTF Textil">DTF Textil</option>
              </select>
            </div>

            <div className="filter-group">
              <span className="filter-label">Tipo</span>
              <select
                className="filter-select"
                value={tipoFilter}
                onChange={(e) => setTipoFilter(e.target.value)}
              >
                <option value="Todos">Todos</option>
                <option value="White">White</option>
                <option value="Black">Black</option>
              </select>
            </div>
          </div>

          {fetchError && (
            <div className="error-banner" role="alert">
              <span className="error-banner-icon">⚠</span>
              <span className="error-banner-msg">{fetchError}</span>
              <button
                type="button"
                className="error-banner-retry"
                onClick={fetchData}
                disabled={loading}
              >
                Reintentar
              </button>
            </div>
          )}

          <main className="content">
            {loading && (
              <div className="loading-overlay show">
                <div className="spinner" />
              </div>
            )}

            <ChunkErrorBoundary>
              <Tabs.Content value="resumen">
                {activeTab === "resumen" && (
                  <Suspense fallback={<PanelSpinner />}>
                    <LazyResumen
                      rows={rawRows}
                      selectedPeriods={selectedPeriods}
                      distribution={distribution}
                    />
                  </Suspense>
                )}
              </Tabs.Content>

              <Tabs.Content value="er">
                {activeTab === "er" && (
                  <Suspense fallback={<PanelSpinner />}>
                    <LazyDistributionPanel distribution={distribution} onChange={setDistribution} />
                    <LazyERTable
                      rows={rawRows}
                      selectedPeriods={selectedPeriods}
                      distribution={distribution}
                      cc={ccFilter}
                      tipo={tipoFilter}
                    />
                  </Suspense>
                )}
              </Tabs.Content>

              <Tabs.Content value="centros">
                {activeTab === "centros" && (
                  <Suspense fallback={<PanelSpinner />}>
                    <LazyCCGrid
                      rows={rawRows}
                      selectedPeriods={selectedPeriods}
                      distribution={distribution}
                      tipo={tipoFilter}
                    />
                  </Suspense>
                )}
              </Tabs.Content>

              <Tabs.Content value="rubros">
                {activeTab === "rubros" && (
                  <Suspense fallback={<PanelSpinner />}>
                    <LazyRubrosTable
                      rows={rawRows}
                      selectedPeriods={selectedPeriods}
                      distribution={distribution}
                      cc={ccFilter}
                      tipo={tipoFilter}
                    />
                  </Suspense>
                )}
              </Tabs.Content>

              <Tabs.Content value="ventas">
                {activeTab === "ventas" && (
                  <Suspense fallback={<PanelSpinner />}>
                    <LazyVentas
                      data={ventasRows}
                      type="ventas"
                      cc={ccFilter}
                      selectedPeriods={selectedPeriods}
                    />
                  </Suspense>
                )}
              </Tabs.Content>

              <Tabs.Content value="compras">
                {activeTab === "compras" && (
                  <Suspense fallback={<PanelSpinner />}>
                    <LazyCompras
                      data={comprasRows}
                      type="compras"
                      cc={ccFilter}
                      selectedPeriods={selectedPeriods}
                    />
                  </Suspense>
                )}
              </Tabs.Content>
            </ChunkErrorBoundary>
          </main>
        </Tabs.Root>
      )}
    </div>
  );
}

export default App;
